import { useMemo } from 'react';
import { ArrowRight, Calendar as CalendarIcon } from 'lucide-react';
import { SchedulePreviewChange } from '@/types/ai';
import { ListSchedule, CalendarEvent } from '@/types/calendar';
import { calendarService } from '@/services/calendarService';
import { useListsStore } from '@/store/listsStore';
import { useGoalsStore } from '@/store/goalsStore';
import { useAspirationsStore } from '@/store/aspirationsStore';
import { devDateTimeService } from '@/services/devDateTimeService';

interface PreviewCalendarEvent extends CalendarEvent {
  isPreview?: boolean;
  previewType?: 'move' | 'new';
}

interface SchedulePreviewCalendarProps {
  changes: SchedulePreviewChange[];
  originalSchedules: ListSchedule[];
}

export function SchedulePreviewCalendar({ changes }: SchedulePreviewCalendarProps) {
  const { lists } = useListsStore();
  const { goals } = useGoalsStore();
  const { aspirations } = useAspirationsStore();

  // Calculate week range
  const weekStart = useMemo(() => {
    const today = devDateTimeService.now();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const weekEnd = useMemo(() => {
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return end;
  }, [weekStart]);

  // Generate current events (before changes)
  const currentEvents = useMemo(() => {
    return calendarService.generateCalendarEvents(weekStart, weekEnd, lists, goals, aspirations);
  }, [weekStart, weekEnd, lists, goals, aspirations]);

  // Build preview events (with changes applied)
  const previewEvents = useMemo(() => {
    const events: PreviewCalendarEvent[] = [];
    const movedScheduleIds = new Set(
      changes.filter(c => c.type === 'move').map(c => c.originalScheduleId)
    );

    // Add unchanged events
    currentEvents.forEach(event => {
      if (!movedScheduleIds.has(event.scheduleId)) {
        events.push({ ...event, isPreview: false });
      }
    });

    // Add moved and new events with preview styling
    changes.forEach(change => {
      const list = lists.find(l => l.id === change.listId);
      if (!list) return;

      const startTime = new Date(change.newDate);
      const [hours, minutes] = change.newTime.split(':').map(Number);
      startTime.setHours(hours, minutes, 0, 0);

      const endTime = new Date(startTime);
      endTime.setMinutes(endTime.getMinutes() + change.duration);

      events.push({
        id: `preview-${change.id}`,
        listId: change.listId,
        scheduleId: change.originalScheduleId || `new-${change.id}`,
        title: change.listTitle,
        description: change.reasoning,
        startTime,
        endTime,
        isAllDay: false,
        listTitle: change.listTitle,
        listStatus: list.status,
        listProgress: 0,
        isRecurring: false,
        color: list.color || '#3B82F6',
        priority: list.priority,
        isPreview: true,
        previewType: change.type
      });
    });

    events.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
    return events;
  }, [currentEvents, changes, lists]);

  // Group events by day
  const dayColumns = useMemo(() => {
    const days: { date: Date; events: PreviewCalendarEvent[] }[] = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(date.getDate() + i);

      days.push({
        date,
        events: previewEvents.filter(e =>
          e.startTime.toDateString() === date.toDateString()
        )
      });
    }

    return days;
  }, [weekStart, previewEvents]);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="space-y-4">
      {/* Legend */}
      <div className="flex items-center gap-4 text-sm flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-100 border-2 border-blue-500 border-dashed rounded"></div>
          <span className="text-muted-foreground">Moved</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-100 border-2 border-green-500 border-dashed rounded"></div>
          <span className="text-muted-foreground">New</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-100 border border-gray-300 rounded"></div>
          <span className="text-muted-foreground">Unchanged</span>
        </div>
      </div>

      {/* Calendar Grid - Desktop */}
      <div className="hidden md:block">
        <div className="grid grid-cols-7 gap-2">
          {/* Day Headers */}
          {dayColumns.map((day) => {
            const isToday = day.date.toDateString() === devDateTimeService.now().toDateString();

            return (
              <div
                key={`header-${day.date.toISOString()}`}
                className={`text-center p-2 font-medium text-sm ${isToday ? 'text-blue-600' : 'text-muted-foreground'}`}
              >
                <div>{dayNames[day.date.getDay()]}</div>
                <div className={`text-lg ${isToday ? 'bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center mx-auto' : ''}`}>
                  {day.date.getDate()}
                </div>
              </div>
            );
          })}

          {/* Day Columns with Events */}
          {dayColumns.map((day) => (
            <div
              key={`events-${day.date.toISOString()}`}
              className="min-h-[200px] border rounded-lg p-2 space-y-1 bg-background"
            >
              {day.events.map((event) => (
                <PreviewEventCard key={event.id} event={event} formatTime={formatTime} />
              ))}

              {day.events.length === 0 && (
                <div className="flex flex-col items-center justify-center text-muted-foreground text-xs py-8">
                  <CalendarIcon className="h-6 w-6 mb-1 opacity-50" />
                  No events
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Calendar Grid - Mobile */}
      <div className="md:hidden space-y-3">
        {dayColumns.map((day) => {
          const isToday = day.date.toDateString() === devDateTimeService.now().toDateString();

          if (day.events.length === 0) return null;

          return (
            <div key={`mobile-${day.date.toISOString()}`} className="border rounded-lg overflow-hidden">
              <div className={`p-3 border-b ${isToday ? 'bg-blue-50' : 'bg-muted/50'}`}>
                <div className="flex items-center gap-2">
                  <span className={`text-lg font-semibold ${isToday ? 'text-blue-600' : ''}`}>
                    {day.date.getDate()}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {dayNames[day.date.getDay()]}
                  </span>
                  {isToday && (
                    <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">Today</span>
                  )}
                </div>
              </div>
              <div className="p-3 space-y-2">
                {day.events.map((event) => (
                  <PreviewEventCard key={event.id} event={event} formatTime={formatTime} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Change Summary */}
      <div className="border rounded-lg p-4 bg-muted/50">
        <h4 className="font-medium mb-3">Changes Summary</h4>
        <div className="space-y-2">
          {changes.map(change => (
            <div key={change.id} className="flex items-center gap-2 text-sm flex-wrap">
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                change.type === 'new' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
              }`}>
                {change.type === 'new' ? 'NEW' : 'MOVE'}
              </span>
              <span className="font-medium">{change.listTitle}</span>
              {change.type === 'move' && change.originalDate && (
                <>
                  <span className="text-muted-foreground">
                    {new Date(change.originalDate).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} {change.originalTime}
                  </span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </>
              )}
              <span className="text-green-600">
                {new Date(change.newDate).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} {change.newTime}
              </span>
            </div>
          ))}
          {changes.length === 0 && (
            <p className="text-sm text-muted-foreground">No changes to apply</p>
          )}
        </div>
      </div>
    </div>
  );
}

interface PreviewEventCardProps {
  event: PreviewCalendarEvent;
  formatTime: (date: Date) => string;
}

function PreviewEventCard({ event, formatTime }: PreviewEventCardProps) {
  const isPreview = event.isPreview;
  const previewType = event.previewType;

  return (
    <div
      className={`p-2 rounded text-xs transition-all ${
        isPreview
          ? previewType === 'new'
            ? 'bg-green-50 border-2 border-green-500 border-dashed'
            : 'bg-blue-50 border-2 border-blue-500 border-dashed'
          : 'bg-gray-50 border border-gray-200'
      }`}
      style={!isPreview ? { borderLeftColor: event.color, borderLeftWidth: '3px' } : {}}
    >
      <div className="font-medium truncate">{event.title}</div>
      <div className="text-muted-foreground">{formatTime(event.startTime)}</div>
      {isPreview && (
        <div className={`text-xs mt-1 italic ${previewType === 'new' ? 'text-green-600' : 'text-blue-600'}`}>
          {previewType === 'new' ? 'New schedule' : 'Rescheduled'}
        </div>
      )}
    </div>
  );
}
